import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

import { CommunicationType, ContentTypeForComparison } from 'domains/content/types';
import useCreateDirectMail from 'hooks/mutations/useCreateDirectMail';
import useUpdateDirectMail from 'hooks/mutations/useUpdateDirectMail';
import { URLPaths } from 'models/enums';
import { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText/ContentStatusText';
import { getContentCreatePath } from 'pages/ContentPage/ContentTable/utils';

type DirectMailPayload = {
    applicationsAccess: string;
    communicationType: CommunicationType;
    name: string;
    parentFolderId?: number;
    _saveMode?: 'save' | 'exit';
    status?: ContentStatus;
};

type UseDirectMailSubmissionProps = {
    applicationsAccess: string[];
    communicationType: CommunicationType;
    id: string | null;
    parentFolderId: number;
    isCreate: boolean;
    isCopy: boolean;
    setErrorMessage: (message: string) => void;
};

const useDirectMailSubmission = ({
    applicationsAccess,
    communicationType,
    id,
    parentFolderId,
    isCreate,
    isCopy,
    setErrorMessage,
}: UseDirectMailSubmissionProps) => {
    const history = useHistory();
    const queryClient = useQueryClient();

    // Success callback for API operations
    const onSuccess = useCallback(
        (data: any, variables: DirectMailPayload) => {
            queryClient.invalidateQueries({
                predicate: (query) => query.queryKey[0] === 'direct-mail',
            });

            if (isCreate || isCopy) {
                history.replace(
                    getContentCreatePath({
                        contentType: ContentTypeForComparison.DIRECT_MAIL,
                        id: data.id,
                        parentFolderId,
                    })
                );
            }

            if (variables._saveMode === 'exit') {
                const contentPath = parentFolderId ? `${URLPaths.CONTENT_FOLDER}/${parentFolderId}` : URLPaths.CONTENT;
                history.push(contentPath);
            }
        },
        [queryClient, isCreate, isCopy, history, parentFolderId]
    );

    // API mutations
    const { mutate: createDirectMail, isLoading: isCreateLoading, error: createError } = useCreateDirectMail({
        onSuccess,
        onError: (error) => {
            setErrorMessage(error.message || 'Failed to save direct mail content');
        },
    });

    const { mutate: updateDirectMail, isLoading: isUpdateLoading, error: updateError } = useUpdateDirectMail(id, {
        onSuccess,
        onError: (error) => {
            setErrorMessage(error.message || 'Failed to update direct mail content');
        },
    });

    // Save handlers
    const handleSave = useCallback(
        ({
            design,
            html,
            name,
            isNameTooShort,
            isUpdate,
            saveMode = 'save',
            status,
            htmlMultilingual,
            designMultilingual,
        }: {
            design: any;
            html: string;
            name: string;
            isNameTooShort: boolean;
            isUpdate: boolean;
            saveMode: 'save' | 'exit';
            status?: ContentStatus;
            htmlMultilingual?: Record<string, string>;
            designMultilingual?: Record<string, any>;
        }) => {
            if (!name || isNameTooShort) {
                if (saveMode === 'exit') {
                    setErrorMessage('Name must be at least 3 characters');
                }
                return;
            }

            const directMailData = {
                design,
                html,
                name,
                _saveMode: saveMode,
                communicationType,
                applicationsAccess: applicationsAccess.join(','),
                ...(status && { status }),
                ...(htmlMultilingual && { htmlMultilingual }),
                ...(designMultilingual && { designMultilingual }),
            };

            if (isUpdate) {
                updateDirectMail({ id, ...directMailData });
            } else {
                const createData = {
                    ...directMailData,
                    ...(parentFolderId ? { parentFolderId } : {}),
                };
                createDirectMail(createData);
            }
        },
        [setErrorMessage, updateDirectMail, id, parentFolderId, createDirectMail, applicationsAccess, communicationType]
    );

    return {
        createDirectMail,
        updateDirectMail,
        isCreateLoading,
        isUpdateLoading,
        createError,
        updateError,
        handleSave,
    };
};

export default useDirectMailSubmission;
